// @ts-nocheck
import { formGlobals } from './_/form-globals';
import BaseInput from './_/base-input';

/**
 * Class: ComboboxInput
 * Purpose: Manage an ARIA combobox (button + listbox), track selection/validity, sync to hidden input,
 *          compose aria-describedby (note/error), and emit events like other inputs.
 */
export default class ComboboxInput extends BaseInput {
  constructor(wrap, formIndex = null, wrapIndex = null) {
    super(wrap, formIndex, wrapIndex);

    // structure
    this.button = wrap.querySelector('[role="combobox"]');
    this.buttonText = this.button?.querySelector('span') || this.button;
    this.listbox = wrap.querySelector('[role="listbox"]');
    this.options = Array.from(wrap.querySelectorAll('[role="option"]'));
    this.announcement = wrap.querySelector('[data-element="announcement"]') || null;
    this.hiddenInput = wrap.querySelector('input[type="hidden"]') || null;

    // ids
    this.inputId   = `combobox-${formIndex}-${wrapIndex}`;
    this.listboxId = `${this.inputId}-listbox`;
    this.errorId   = `${this.inputId}-error`;

    this._initErrorElement();
    this._initNoteElement(this.wrap.querySelector(formGlobals.noteSelector) || null);

    this.required = this.button?.getAttribute('aria-required') === 'true';

    // state
    this.open = false;
    this.blurred = false;
    this.valid = this.required ? false : true;
    this.currentIndex = 0;
    this.value = '';
    this.label = '';

    this.placeholder = this.button?.value || this.buttonText?.textContent?.trim() || '';

    // bindings
    this._onDocClick = this._onDocClick.bind(this);
    this._onButtonKeydown = this._onButtonKeydown.bind(this);
    this._onListboxInteraction = this._onListboxInteraction.bind(this);
    this._onButtonClick = this._onButtonClick.bind(this);
    this._onButtonFocus = this._onButtonFocus.bind(this);
    this._onButtonBlur  = this._onButtonBlur.bind(this);

    this._setAttributes();
    this._setDefaultSelectionFromMarkup();
    this._initEvents();
  }

  _getAriaElement() {
    return this.button;
  }

  _getValidChangeDetail(): { valid: boolean; value: string; label: string; instance: any } {
    return { valid: this.valid, value: this.value, label: this.label, instance: this };
  }

  // -------- Public API --------
  isValid() { return this.valid; }

  refreshOptions() {
    if (!this.listbox) return;
    this.options = Array.from(this.listbox.querySelectorAll('[role="option"]'));
    this._setAttributes();
    this._setDefaultSelectionFromMarkup();
  }

  getState() {
    return {
      value: this.value,
      label: this.label,
      valid: this.valid,
      blurred: this.blurred,
      required: this.required,
      id: this.inputId,
      open: this.open
    };
  }

  validateNow() { this._validate(); }
  openList() { if (!this.open) this._toggle(true); }
  closeList() { if (this.open)  this._toggle(false); }

  destroy() {
    super.destroy();
    document.removeEventListener('click', this._onDocClick, true);
    this.button = null;
    this.buttonText = null;
    this.listbox = null;
    this.options = [];
    this.hiddenInput = null;
    this.announcement = null;
  }

  // -------- Private --------
  _setAttributes() {
    if (!this.button || !this.listbox) return;

    this.button.id = this.inputId;
    this.listbox.id = this.listboxId;
    this.button.setAttribute('aria-controls', this.listboxId);
    this.button.setAttribute('aria-expanded', 'false');
    
    // Ensure the button is type="button" to prevent form submission
    // Without this, the button defaults to type="submit" which can cause 
    // the combobox to open when Enter is pressed in other form fields
    if (this.button.tagName === 'BUTTON' && !this.button.hasAttribute('type')) {
      this.button.setAttribute('type', 'button');
    }

    this._setAriaDescribedBy({ includeNote: true, includeError: false });

    this.options.forEach((opt, i) => {
      if (!opt.id) opt.id = `${this.inputId}-option-${i}`;
      if (!opt.hasAttribute('role')) opt.setAttribute('role', 'option');
      if (!opt.hasAttribute('aria-selected')) opt.setAttribute('aria-selected', 'false');
    });
  }

  _initEvents() {
    if (!this.button || !this.listbox) return;
    const o = { signal: this._ac.signal };

    this.button.addEventListener('click', this._onButtonClick, o);
    this.button.addEventListener('keydown', this._onButtonKeydown, o);
    this.button.addEventListener('focus', this._onButtonFocus, o);
    this.button.addEventListener('blur',  this._onButtonBlur,  o);

    // Use mousedown instead of click to prevent focus loss issues
    this.listbox.addEventListener('mousedown', this._onListboxInteraction, o);

    document.addEventListener('click', this._onDocClick, true);
  }

  _onButtonFocus() {
    this.wrap.classList.add(formGlobals.focusedClass);
    this._dispatch('focus', { instance: this });
  }

  _onButtonBlur(e) {
    // If the new focus is within the listbox, don't close yet
    if (e.relatedTarget && this.listbox.contains(e.relatedTarget)) return;

    this.wrap.classList.remove(formGlobals.focusedClass);
    const wasFirst = !this.blurred;
    this.blurred = true;
    
    if (this.open) {
      this._toggle(false);
    }
    
    this._dispatch('blur', { instance: this });
    if (wasFirst && this.required) this._validate();
  }

  _onButtonClick(e) {
    e.preventDefault();
    this._toggle(!this.open);
  }

  _onDocClick(e) {
    if (!this.open) return;
    if (!this.wrap.contains(e.target)) {
      this._toggle(false);
      if (this.required) this._validate();
    }
  }

  _onButtonKeydown(e) {
    const { key } = e;

    if (key === 'Tab') {
      if (this.open) this._toggle(false);
      return;
    }

    if (!this.open && (key === 'ArrowDown' || key === 'ArrowUp' || key === 'Enter' || key === ' ')) {
      e.preventDefault();
      return this._toggle(true);
    }

    if (!this.open) return;

    switch (key) {
      case 'Escape':
        e.preventDefault();
        this._toggle(false);
        break;
      case 'ArrowDown':
        e.preventDefault();
        this._move(1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        this._move(-1);
        break;
      case 'Home':
        e.preventDefault();
        this._setCurrentIndex(0, true);
        break;
      case 'End':
        e.preventDefault();
        this._setCurrentIndex(this.options.length - 1, true);
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        this._selectIndex(this.currentIndex);
        this._toggle(false);
        break;
      default:
        if (key.length === 1) {
          const lower = key.toLowerCase();
          const idx = this._findByPrefix(lower, this.currentIndex + 1);
          if (idx !== -1) this._setCurrentIndex(idx, true);
        }
    }
  }

  _onListboxInteraction(e) {
    // Prevent the listbox interaction from causing a blur on the button
    e.preventDefault();
    
    const opt = e.target.closest('[role="option"]');
    if (!opt) return;
    const idx = this.options.indexOf(opt);
    if (idx === -1) return;
    this._selectIndex(idx);
    this._toggle(false);
  }

  _toggle(nextOpen) {
    this.open = !!nextOpen;
    this.listbox.classList.toggle('is-open', this.open);
    this.button.setAttribute('aria-expanded', String(this.open));
    this._dispatch('openchange', { open: this.open, instance: this });

    if (this.open) {
      const cur = this.options[this.currentIndex];
      if (cur) {
        this.button.setAttribute('aria-activedescendant', cur.id);
        cur.scrollIntoView({ block: 'nearest' });
      }
    } else {
      this.options.forEach(o => o.classList.remove('is-focused'));
      this.button.removeAttribute('aria-activedescendant');
      if (this.required) this._validate();
    }
  }

  _move(delta) {
    if (!this.options.length) return;
    const next = (this.currentIndex + delta + this.options.length) % this.options.length;
    this._setCurrentIndex(next, true);
  }

  _setCurrentIndex(idx, announce = false) {
    this.currentIndex = Math.max(0, Math.min(idx, this.options.length - 1));
    const cur = this.options[this.currentIndex];
    this.options.forEach((o, i) => o.classList.toggle('is-focused', i === this.currentIndex));
    this.button.setAttribute('aria-activedescendant', cur.id);
    // Scroll to ensure the focused option is visible
    if (cur) cur.scrollIntoView({ block: 'nearest' });
    if (announce) this._announce(`Focus: ${this._labelOf(cur)}`);
  }

  _selectIndex(idx) {
    const opt = this.options[idx];
    if (!opt) return;
    this.options.forEach(o => { o.classList.remove('is-selected'); o.setAttribute('aria-selected', 'false'); });
    opt.classList.add('is-selected');
    opt.setAttribute('aria-selected', 'true');

    const label = this._labelOf(opt);
    const value = this._valueOf(opt);

    this.button.value = label;
    if (this.buttonText) this.buttonText.textContent = label;
    if (this.hiddenInput) this.hiddenInput.value = value;

    this.value = value;
    this.label = label;

    if (this.label) this.wrap.classList.add(formGlobals.filledClass);
    else this.wrap.classList.remove(formGlobals.filledClass);

    this._dispatch('valuechange', { value, label, instance: this });
    this._validate();
    this._announce(label);
  }

  _setDefaultSelectionFromMarkup() {
    const idx = this.options.findIndex(o => o.getAttribute('aria-selected') === 'true' || o.classList.contains('is-selected'));
    if (idx >= 0) {
      this.currentIndex = idx;
      this._selectIndex(idx);
    } else {
      this.wrap.classList.toggle(formGlobals.filledClass, !!this.button.value || !!this.buttonText?.textContent?.trim());
    }
  }

  _validate() {
    let isValid;
    if (!this.required) {
      isValid = true;
    } else {
      const current = (this.hiddenInput && this.hiddenInput.value) || this.button.value || '';
      isValid = current.trim() !== '' && current.trim() !== this.placeholder.trim();
    }
    if (isValid) this._setValid(); else this._setInvalid();
  }

  _labelOf(opt) {
    const span = opt.querySelector('span');
    return (span ? span.textContent : opt.textContent || '').trim();
  }

  _valueOf(opt) {
    const v = opt.getAttribute('data-value');
    return v !== null ? v : this._labelOf(opt);
  }

  _announce(text) {
    if (!this.announcement) return;
    this.announcement.textContent = text;
    this.announcement.setAttribute('aria-live', 'assertive');
    clearTimeout(this._announceTO);
    this._announceTO = setTimeout(() => {
      if (!this.announcement) return;
      this.announcement.textContent = '';
      this.announcement.setAttribute('aria-live', 'off');
    }, 900);
  }
}
